import type { ObjectType, NameProperties } from '../../components/parser/ParserTypes';
import { type ObjectWithPillsMap } from '../../components/documentation/salesforce-documentation-card/types';
import type { DocumentationPills } from '../global/globalReducerTypes';
import { createSlice, PayloadAction } from '@reduxjs/toolkit';
import { RootState } from '..';
import { DocumentationTabTypes } from '../../types/enums/DocumentationTabTypes';
import { getObjectsWithPillsPerCategory } from '../../components/documentation/cpq/utils';
import { formatCamelCase } from '../../components/documentation/universal-search/utils';
import isEqual from 'lodash/isEqual';
import flatten from 'lodash/flatten';
import uniq from 'lodash/uniq';
import { sortLexicographically } from '../../lib/sortLexicographically';
import { translateTypeToHumanName } from '../../components/documentation/helpers';
import { SalesforceItem } from '../../components/parser/ParserTypes';
import { RelatedListProperties } from '@server/documentation';

export interface LayoutRelatedListsAndButtons {
  lists: RelatedListProperties[];
  buttons: NameProperties[];
}

export interface SalesforceDocumentationState {
  objects: ObjectWithPillsMap;
  transientObjects: { [crmOrgId: string]: { [objectName: string]: boolean } };
  singleObject?: {
    activeTab?: DocumentationTabTypes;
    objectTypeName?: ObjectTypeName;
  };
  activeObjectElementsRawData?: {
    [key in DocumentationTabTypes]: SalesforceItem[];
  };
  relatedObjectFilterOptions?: { label: string; value: string }[];
  configurationTypeFilterOptions?: { label: string; value: string }[];
  relatedListsAndButtons: { [metadataId: string]: LayoutRelatedListsAndButtons };
  showLoader: boolean;
}

const initialState: SalesforceDocumentationState = {
  objects: {
    favorites: [],
    standardObjects: [],
    cpqObjects: [],
    customObjects: [],
    platformEvents: [],
  },
  singleObject: undefined,
  transientObjects: {},
  relatedListsAndButtons: {},
  showLoader: false,
};

export const salesforceDocumentationSlice = createSlice({
  name: 'salesforceDocumentation',
  initialState,
  reducers: {
    setObjects: (
      state,
      action: PayloadAction<{
        recordTypesData: RecordTypesData;
        funnelsData: FunnelsData;
        objectTypeNames: ObjectTypeName[];
        pills?: DocumentationPills;
        transientObjects: { [objectName: string]: boolean };
        parsedObjects?: ObjectType[];
      }>,
    ) => {
      const {
        funnelsData,
        recordTypesData,
        objectTypeNames,
        pills,
        transientObjects,
        parsedObjects,
      } = action.payload;

      const {
        cpq,
        favorites,
        customObjectsNotInFM,
        platformEventsNotInFM,
        standardObjectsNotInFunnelMap,
      } = getObjectsWithPillsPerCategory({
        recordTypesData,
        funnelsData,
        objectTypeNames,
        pills,
        transientObjects,
        parsedObjects,
      });

      state.objects = {
        favorites,
        standardObjects: standardObjectsNotInFunnelMap,
        cpqObjects: cpq,
        customObjects: customObjectsNotInFM,
        platformEvents: platformEventsNotInFM,
      };
    },
    setObjectName: (
      state,
      action: PayloadAction<{
        objectTypeName?: ObjectTypeName;
      }>,
    ) => {
      const { objectTypeName } = action.payload;

      state.singleObject = {
        objectTypeName: objectTypeName,
        activeTab: DocumentationTabTypes.CARDS_LIST,
      };
    },
    setSingleObjectName: (
      state,
      action: PayloadAction<{
        singleObjectName?: ObjectTypeName;
        tab: DocumentationTabTypes;
      }>,
    ) => {
      const { singleObjectName, tab } = action.payload;

      if (!singleObjectName) {
        state.singleObject = undefined;
        return;
      }

      state.singleObject = {
        objectTypeName: singleObjectName,
        activeTab: tab,
      };
    },
    clearDocumentationDialog: (state) => ({
      ...initialState,
      objects: state.objects,
    }),
    openPredefinedStepRulesInSingleObjectScreen: (
      state,
      action: PayloadAction<{
        singleObjectApiName: string;
        defaultActiveTab: DocumentationTabTypes;
        objects?: ObjectTypeName[];
      }>,
    ) => {
      const { singleObjectApiName, defaultActiveTab, objects } = action.payload;

      state.singleObject = {
        objectTypeName: objects?.find((object) => object.objectType === singleObjectApiName),
        activeTab: defaultActiveTab,
      };
    },
    handleOnPillClick: (
      state,
      action: PayloadAction<{
        tab: DocumentationTabTypes;
        singleObjectName: string;
      }>,
    ) => {
      state.singleObject = {
        objectTypeName: {
          objectType: action.payload.singleObjectName,
          label: action.payload.singleObjectName,
        },
        activeTab: action.payload.tab,
      };
    },
    setTab: (
      state,
      action: PayloadAction<{
        tab: DocumentationTabTypes;
      }>,
    ) => {
      state.singleObject = {
        ...state.singleObject,
        activeTab: action.payload.tab,
      };
    },
    setShowLoader: (state, action: PayloadAction<{ showLoader: boolean }>) => {
      state.showLoader = action.payload.showLoader;
    },
    setTransientObjects: (
      state,
      action: PayloadAction<{
        objectName: string;
        crmOrgId: string;
        isObjectBeingParsed: boolean;
      }>,
    ) => {
      const { crmOrgId, objectName, isObjectBeingParsed } = action.payload;

      if (!state.transientObjects[crmOrgId]) {
        state.transientObjects[crmOrgId] = {};
      }

      state.transientObjects[crmOrgId][objectName] = isObjectBeingParsed;
    },
    setActiveObjectElementsRawData: (
      state,
      action: PayloadAction<{
        list: { [key in DocumentationTabTypes]: SalesforceItem[] };
      }>,
    ) => {
      const { list } = action.payload;
      state.activeObjectElementsRawData = list;

      const newObjectOptions: { label: string; value: string }[] = uniq(
        flatten(
          list[DocumentationTabTypes.RELATED_OBJECTS]?.map(
            (relatedObject) => relatedObject.objectApiNames ?? [],
          ),
        ),
      )
        .filter((item) => !!item)
        .map((item) => ({ label: formatCamelCase(item ?? ''), value: item }));

      const configurationTypeOptions: { label: string; value: string }[] = uniq(
        flatten(
          list[DocumentationTabTypes.RELATED_OBJECTS]?.map(
            (relatedObject) => relatedObject.parentType,
          ),
        ),
      )
        .filter((item) => !!item)
        .map((item) => ({
          label: translateTypeToHumanName(item),
          value: item,
        }));

      const sortedNewObjectOptions = sortLexicographically({
        items: newObjectOptions,
        sortKey: 'label',
      });

      const sortedConfigurationTypeOptions = sortLexicographically({
        items: configurationTypeOptions,
        sortKey: 'label',
      });

      if (!isEqual(sortedNewObjectOptions, state.relatedObjectFilterOptions)) {
        state.relatedObjectFilterOptions = sortedNewObjectOptions;
      }

      if (!isEqual(sortedConfigurationTypeOptions, state.configurationTypeFilterOptions)) {
        state.configurationTypeFilterOptions = sortedConfigurationTypeOptions;
      }
    },
    setRelatedListsAndButtons: (
      state,
      action: PayloadAction<{
        metadataId: string;
        lists: RelatedListProperties[];
        buttons: NameProperties[];
      }>,
    ) => {
      const { metadataId, lists, buttons } = action.payload;
      state.relatedListsAndButtons[metadataId] = { lists, buttons };
    },
  },
});

export const {
  setSingleObjectName,
  clearDocumentationDialog,
  openPredefinedStepRulesInSingleObjectScreen,
  setTab,
  handleOnPillClick,
  setActiveObjectElementsRawData,

  setObjectName,
  setObjects,

  setShowLoader,
  setTransientObjects,
  setRelatedListsAndButtons,
} = salesforceDocumentationSlice.actions;

export const selectTransientObjects = (crmOrgId: string) => (state: RootState) =>
  state.salesforceDocumentation.transientObjects[crmOrgId];

export const selectLoader = (crmOrgId: string) => (state: RootState) =>
  state.salesforceDocumentation.showLoader &&
  !state.global.environments[crmOrgId]?.data?.documentation;

export const selectSingleObject = (state: RootState) =>
  state.salesforceDocumentation.singleObject?.objectTypeName;

export const selectSingleObjectActiveTab = (state: RootState) =>
  state.salesforceDocumentation.singleObject?.activeTab;

export const selectObjects = (state: RootState) => state.salesforceDocumentation.objects;

export const selectDocumentationActiveObjectData = (state: RootState) =>
  state.salesforceDocumentation.activeObjectElementsRawData;

export const selectDocumentationActiveTabElements = (state: RootState) =>
  state.salesforceDocumentation.activeObjectElementsRawData?.[
    state.salesforceDocumentation.singleObject
      ?.activeTab as keyof typeof state.salesforceDocumentation.activeObjectElementsRawData
  ];

export const selectRelatedObjectFilterOptions = (state: RootState) =>
  state.salesforceDocumentation.relatedObjectFilterOptions;

export const selectRelatedObjectConfigurationTypeFilterOptions = (state: RootState) =>
  state.salesforceDocumentation.configurationTypeFilterOptions;

export const selectRelatedListsAndButtons = (metadataId: string) => (state: RootState) =>
  state.salesforceDocumentation.relatedListsAndButtons[metadataId];

export default salesforceDocumentationSlice.reducer;
